import { Box, Portal } from '@mui/material';
import { useState, useLayoutEffect } from 'react';

import MessageCenter from './MessageCenter';
import ReferralButton from './ReferralButton';
import HelpAgentButton from './HelpAgent/HelpAgentButton';

import Inbox from 'modules/Inbox';
import { FeatureGates } from 'modules/common/constants/featuregates';
import useStatsigGate from 'modules/common/useStatsigGate';
import { useImpactAdvocateEnabled } from 'modules/common/hooks/useImpactAdvocateEnabled';
import useMessageCenterCopy from 'modules/IMC/common/hooks/useMessageCenterCopy';

interface MessagesItemProps {
    includePortal: boolean;
    hasPaymentIssue: boolean;
    isProjectInboxEnabled: boolean;
    menuRect: DOMRect | null;
}

const MENU_OFFSET = 14;

function MessagesItem({
    includePortal,
    hasPaymentIssue,
    isProjectInboxEnabled,
    menuRect,
}: Readonly<MessagesItemProps>) {
    // With a payment issue this control leaves the header flow: it portals to the body and pins
    // itself to the left of the user menu, while HelpAgentItem holds its place with a 160px
    // margin. That mode is therefore always Help then Messages, whichever HUB-2109 arm is on,
    // which is both today's behaviour (so control matches main) and the order the experiment
    // wants. Do not wire isHelpFirst into it: the only thing that could change is control.
    return (
        <Portal disablePortal={!hasPaymentIssue}>
            <Box
                className="item"
                sx={{
                    ...(hasPaymentIssue && {
                        zIndex: ({ zIndex }) => zIndex.modal + 1,
                        top: 16, // Since its in portal, instead of using 1 to 8px its using 1 to 1px mapping.
                        right: `${window.innerWidth - (menuRect?.left || 0) + MENU_OFFSET}px`,
                        position: 'fixed',
                    }),
                }}>
                {isProjectInboxEnabled ? (
                    <Inbox />
                ) : (
                    <MessageCenter includePortal={includePortal} hasPaymentIssue={hasPaymentIssue} />
                )}
            </Box>
        </Portal>
    );
}

function HelpAgentItem({ hasPaymentIssue }: Readonly<{ hasPaymentIssue: boolean }>) {
    return (
        <Box className="item" sx={{ ...(hasPaymentIssue && { '&.item': { marginRight: '160px' } }) }}>
            <HelpAgentButton />
        </Box>
    );
}

function IMC({
    includePortal = true,
    hasPaymentIssue = false,
    disableReferralButton = false,
}: {
    includePortal?: boolean;
    hasPaymentIssue?: boolean;
    disableReferralButton?: boolean;
}) {
    const isImpactAdvocateEnabled = useImpactAdvocateEnabled();
    const isProjectInboxEnabled = useStatsigGate(FeatureGates.ProjectInboxPoc);
    const { isHelpFirst } = useMessageCenterCopy();
    const [menuRect, setMenuRect] = useState<DOMRect | null>(null);

    useLayoutEffect(() => {
        if (hasPaymentIssue) {
            const userMenu = document.querySelector('button[aria-label="User menu"]');
            if (userMenu) {
                const userMenuRect = userMenu.getBoundingClientRect();
                setMenuRect(userMenuRect);
            }
        }
    }, [hasPaymentIssue]);

    // Keyed so the HUB-2109 swap reorders these instances rather than remounting the Message
    // Center and its drawer.
    const messagesItem = (
        <MessagesItem
            key="messages"
            includePortal={includePortal}
            hasPaymentIssue={hasPaymentIssue}
            isProjectInboxEnabled={isProjectInboxEnabled}
            menuRect={menuRect}
        />
    );
    const helpAgentItem = isProjectInboxEnabled ? null : (
        <HelpAgentItem key="help-agent" hasPaymentIssue={hasPaymentIssue} />
    );

    return (
        <>
            {isImpactAdvocateEnabled && !disableReferralButton && (
                <Box className="item" sx={{ ...(hasPaymentIssue && { marginRight: '160px' }) }}>
                    <ReferralButton />
                </Box>
            )}
            {isHelpFirst ? [helpAgentItem, messagesItem] : [messagesItem, helpAgentItem]}
        </>
    );
}

export default IMC;
